import React, { useState } from 'react';
import api from '../../services/api';

const logoImage = '/assets/logo.png';
const API_BASE  = (process.env.REACT_APP_API_URL || 'https://cmsapi3.vdavns.com/api').replace('/api', '');

export default function PublicDownload() {
  const [appNo, setAppNo]     = useState('');
  const [mobile, setMobile]   = useState('');
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setError(''); setResult(null);
    if (!appNo.trim()) return setError('Please enter application number');
    if (!mobile || mobile.length !== 10) return setError('Please enter valid 10-digit mobile number');
    setLoading(true);
    try {
      const r = await api.get(`/public/noc-download?app_no=${encodeURIComponent(appNo.trim())}&mobile=${mobile.trim()}`);
      if (r.success && r.noc_pdf) {
        setResult(r);
      } else {
        setError(r.message || 'NOC not found or not approved yet.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Application not found. Please check the details.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #1a365d 0%, #2b6cb0 60%, #1e40af 100%)' }}>

      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

      <div className="relative z-10 w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4 overflow-hidden">
            <img src={logoImage} alt="VDA" className="w-16 h-16 object-contain"
              onError={e => { e.target.style.display='none'; e.target.parentNode.style.background='#c53030'; e.target.parentNode.innerHTML='<span style="color:white;font-weight:900;font-size:1.5rem">VDA</span>'; }} />
          </div>
          <h1 className="text-2xl font-bold text-white">Download NOC Certificate</h1>
          <p className="text-blue-200 text-sm mt-1">Varanasi Development Authority</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">

          {!result ? (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800">Verify & Download</h2>
                <p className="text-gray-500 text-sm mt-1">Enter your application number and registered mobile</p>
              </div>

              <form onSubmit={handleSearch} className="space-y-4">
                <div>
                  <label className="form-label">Application Number <span className="text-red-500">*</span></label>
                  <input type="text" className="form-input uppercase"
                    placeholder="e.g. NOC-202604-123456"
                    value={appNo}
                    onChange={e => { setAppNo(e.target.value.toUpperCase()); setError(''); }}
                    required autoFocus />
                </div>

                <div>
                  <label className="form-label">Registered Mobile Number <span className="text-red-500">*</span></label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 text-gray-500 text-sm font-medium">+91</span>
                    <input type="tel" className="form-input rounded-l-none flex-1"
                      placeholder="10-digit mobile number"
                      value={mobile}
                      onChange={e => { setMobile(e.target.value.replace(/\D/,'').slice(0,10)); setError(''); }}
                      maxLength={10} required />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    ❌ {error}
                  </div>
                )}

                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #1a365d, #2b6cb0)' }}>
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : '🔍 Verify & Get NOC'}
                </button>
              </form>

              <p className="text-center text-xs text-gray-400 mt-4">
                Only approved NOC applications can be downloaded
              </p>
            </>
          ) : (
            /* Success — show NOC download */
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-4xl mx-auto mb-4">🏆</div>
              <h2 className="text-xl font-bold text-green-700 mb-1">NOC Approved!</h2>
              <p className="text-gray-500 text-sm mb-6">Your certificate is ready to download</p>

              <div className="bg-gray-50 rounded-xl p-4 text-left mb-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Application No:</span>
                  <span className="font-semibold text-blue-700 font-mono">{result.application_no}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">NOC Type:</span>
                  <span className="font-semibold">{result.noc_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Applicant:</span>
                  <span className="font-semibold">{result.applicant_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Approved On:</span>
                  <span className="font-semibold">{result.approved_at ? new Date(result.approved_at).toLocaleDateString('en-IN') : '—'}</span>
                </div>
              </div>

              <a href={`${API_BASE}${result.noc_pdf}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white font-semibold text-sm"
                style={{ background: 'linear-gradient(135deg, #065f46, #10b981)' }}>
                📄 Download NOC Certificate
              </a>

              <button onClick={() => { setResult(null); setAppNo(''); setMobile(''); }}
                className="mt-3 w-full py-2 text-sm text-gray-500 hover:text-gray-700">
                ← Search Another Application
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-blue-200 text-xs mt-5 opacity-70">
          © {new Date().getFullYear()} Varanasi Development Authority
        </p>
      </div>
    </div>
  );
}